<app-side-panel [heading]="heading()" [subtitle]="subtitle()" (closed)="store.closeRun()">
  @if (!options().single) {
    <section class="flex flex-col gap-2 border-b border-border px-5 py-4">
      <div class="flex items-center justify-between gap-2">
        <h3 class="ks-eyebrow">Für welche Analysen?</h3>
        <button type="button" class="text-xs text-primary hover:underline" (click)="store.pickFromTable()">{{ fixed() ? 'Auswahl ändern' : 'In der Tabelle auswählen' }}</button>
      </div>
      @if (fixed()) {
        <p class="flex items-baseline gap-1.5"><span class="font-mono text-dense">{{ options().ids!.length | number: '1.0-0' : 'de-DE' }}</span><span class="text-dense text-muted-foreground">aus der Tabelle ausgewählt</span></p>
      } @else {
        <div class="grid grid-cols-2 gap-x-6 gap-y-3">
          @for (column of columns; track $index) {
            <div class="flex flex-col gap-3">
              @for (group of column; track group.name) {
                <div class="flex flex-col">
                  <span class="px-2 pb-0.5 text-xs text-muted-foreground">{{ group.name }}</span>
                  @for (stage of group.stages; track stage) {
                    <label class="grid h-8 grid-cols-[1rem_0.5rem_minmax(0,1fr)_auto] items-center gap-2 rounded-md px-2 text-dense transition-colors" [class]="store.counts()?.[stage] ? 'cursor-pointer hover:bg-muted' : 'text-muted-foreground opacity-50'" [hlmTooltipTrigger]="meta[stage].tip">
                      <hlm-checkbox [checked]="targets().has(stage)" [disabled]="!store.counts()?.[stage]" [attr.aria-label]="meta[stage].label" (changed)="toggle(stage)" />
                      <span class="size-2 rounded-full" [style.background-color]="meta[stage].color" aria-hidden="true"></span>
                      <span class="truncate">{{ meta[stage].label }}</span>
                      <span class="font-mono text-xs">{{ (store.counts()?.[stage] ?? 0) | number: '1.0-0' : 'de-DE' }}</span>
                    </label>
                  }
                </div>
              }
            </div>
          }
        </div>
      }
    </section>
  }
  @if (!fixed()) {
    <section class="flex flex-col gap-1 border-b border-border px-5 py-4">
      <h3 class="ks-eyebrow pb-1">Wie viele?</h3>
      <div class="flex flex-wrap items-center gap-x-6 gap-y-1" role="radiogroup" aria-label="Wie viele">
        <button type="button" role="radio" class="inline-flex h-8 items-center gap-2 text-dense" [attr.aria-checked]="how() === 'all'" (click)="how.set('all')">
          <span class="size-4 rounded-full" [class]="how() === 'all' ? 'border-[5px] border-primary' : 'border border-ks-grey-300'" aria-hidden="true"></span>
          Alle <span class="font-mono text-xs">{{ targetCount() | number: '1.0-0' : 'de-DE' }}</span>
        </button>
        <div class="inline-flex h-8 cursor-pointer items-center gap-2 text-dense" role="radio" tabindex="0" [attr.aria-checked]="how() === 'n'" (click)="how.set('n')" (keydown.space)="how.set('n')">
          <span class="size-4 rounded-full" [class]="how() === 'n' ? 'border-[5px] border-primary' : 'border border-ks-grey-300'" aria-hidden="true"></span>
          Nur die ersten
          <input type="number" min="1" aria-label="Anzahl" class="h-8 w-20 rounded-md border border-input bg-background px-2 font-mono text-xs outline-none focus:border-ring focus:ring-[3px] focus:ring-ring/25" [value]="limit()" (input)="setLimit($any($event.target).value)" (focus)="how.set('n')" />
        </div>
      </div>
    </section>
  }

  <section class="flex flex-col gap-1 border-b border-border px-5 py-4">
    <div class="flex items-center justify-between gap-2">
      <h3 class="ks-eyebrow">Bis zu welchem Schritt?</h3>
      <label class="inline-flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
        <hlm-switch aria-label="Technische Schritte zeigen" [checked]="technical()" (checkedChange)="technical.set($event); hoveredLast.set(null)" />
        Technische Schritte
      </label>
    </div>
    <app-step-timeline [steps]="steps()" [config]="timelineConfig" (stepHover)="onHover($event)" (stepClick)="pickStep($event)" (nodeActionClick)="pickStep($event)" />
    <p class="text-xs text-muted-foreground">{{ stepHint() }}</p>
  </section>

  @if (needsTemplate()) {
    <section class="flex flex-col gap-1.5 px-5 py-4">
      <label class="ks-eyebrow" for="run-template">Planungsvorlage</label>
      <select id="run-template" class="kenergy-native-select h-10 w-full rounded-sm border border-input bg-background px-2 text-sm" [value]="template()" (change)="templateId.set($any($event.target).value)">
        @for (option of templateOptions(); track option.id) {
          <option [value]="option.id" [selected]="option.id === template()">{{ option.name }}</option>
        }
      </select>
      <p class="text-xs text-muted-foreground">{{ template() === 'keep' ? 'Jede Analyse wird mit der Vorlage neu gerechnet, die sie gerade hat.' : 'Gilt für alle Analysen dieser Berechnung.' }}</p>
    </section>
  }

  <div panelFooter class="flex shrink-0 flex-col gap-3 border-t border-border px-5 py-3">
    @if (runError()) { <app-inline-notice tone="error" [message]="runError()" /> }
    <div aria-live="polite">
      <p class="flex items-baseline gap-1.5">
        <span class="text-lg font-bold tabular-nums">{{ preview() ? (preview()!.runnable | number: '1.0-0' : 'de-DE') : '…' }}</span>
        <span class="text-dense text-muted-foreground">{{ preview()?.runnable === 1 ? 'Analyse' : 'Analysen' }} bis {{ lastStepName() }}</span>
      </p>
      <p class="text-xs text-muted-foreground">
        @if (store.running()) {
          Es läuft bereits eine Berechnung. Danach können Sie die nächste starten.
        } @else if (preview() && !preview()!.runnable) {
          Für diese Auswahl gibt es nichts zu rechnen.
        } @else if (skippedText()) {
          Ausgelassen: {{ skippedText() }}.
        } @else {
          Läuft im Hintergrund. Laufende Analysen werden ausgelassen.
        }
      </p>
    </div>
    <div class="flex justify-end gap-2">
      <button hlmBtn type="button" variant="outline" (click)="panel()?.close()">Abbrechen</button>
      <button hlmBtn type="button" [disabled]="blocked()" (click)="start()">{{ cta() }}</button>
    </div>
  </div>
</app-side-panel>
